import { useTranslation } from "react-i18next";
import ClaimsList from "./claims-list";
import { RxCaretDown } from "react-icons/rx";
import { getName, odsClaimStatusConversion } from "js/formatting";
import { useContext, useMemo, useRef, useState, useEffect } from "react";
import { AgentData } from "context/Agent/DataProvider";
import RowPagination from "components/General/RowPagination";
import { useRowPagination } from "hooks/useRowPagination";

export const ClaimsOverview = ({ data, setOpenChatWindow, type }) => {
  const { t } = useTranslation();
  const { agentPolicies } = useContext(AgentData);
  const [sortConfig, setSortConfig] = useState({
    key: "dateReceived",
    direction: "desc",
  });
  const [dropdownState, setDropdownState] = useState({
    isOpen1: false,
    isOpen2: false,
    selectedStatus: ["All"],
    selectedClaimType: ["All"],
  });

  const dropdownRef1 = useRef(null);
  const dropdownRef2 = useRef(null);

  const toggleDropdown = (dropdown) => {
    setDropdownState((prevState) => ({
      ...prevState,
      isOpen1: dropdown === "isOpen1" ? !prevState.isOpen1 : false,
      isOpen2: dropdown === "isOpen2" ? !prevState.isOpen2 : false,
    }));
  };

  const requestSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction:
        prevConfig.key === key && prevConfig.direction === "asc"
          ? "desc"
          : "asc",
    }));

    if (dropdownState.isOpen1 || dropdownState.isOpen2) {
      setDropdownState((prevState) => ({
        ...prevState,
        isOpen1: false,
        isOpen2: false,
      }));
    }
  };

  const handleSelectionChange = (type, option) => {
    setDropdownState((prevState) => {
      const isAllSelected = option === "All";
      const selectedOptions = isAllSelected
        ? ["All"]
        : prevState[type].includes(option)
        ? prevState[type].filter((item) => item !== option)
        : [...prevState[type].filter((item) => item !== "All"), option];

      return {
        ...prevState,
        [type]: selectedOptions.length === 0 ? ["All"] : selectedOptions,
      };
    });
  };

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const [statusClass, statusText] = odsClaimStatusConversion(
        item.claimStatus,
        item.claimType
      );
      const claimTypeText =
        item.claimType === "DIS" ? "Disability Claim" : "Death Claim";
      const statusMatch =
        dropdownState.selectedStatus.includes("All") ||
        dropdownState.selectedStatus.includes(statusText);
      const claimTypeMatch =
        dropdownState.selectedClaimType.includes("All") ||
        dropdownState.selectedClaimType.includes(claimTypeText);
      return statusMatch && claimTypeMatch;
    });
  }, [data, dropdownState.selectedStatus, dropdownState.selectedClaimType]);

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;
    return [...filteredData].sort((a, b) => {
      const dateA = new Date(a.dateReceived);
      const dateB = new Date(b.dateReceived);
      return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
    });
  }, [filteredData, sortConfig]);

  const uniqueStatusTexts = [
    "All",
    ...new Set(
      data.map((item) => {
        const [statusClass, statusText] = odsClaimStatusConversion(
          item.claimStatus,
          item.claimType
        );
        return statusText;
      })
    ),
  ];

  const uniqueClaimType = [
    "All",
    ...new Set(
      data.map((item) => {
        return item.claimType === "DIS" ? "Disability Claim" : "Death Claim";
      })
    ),
  ];

  const getPrimaryOwnerFullName = (contractID) => {
    const policy = agentPolicies?.find((p) => p.contractID === contractID);
    const party = policy?.parties?.find(
      (x) => x.person?.type?.toLowerCase() === "primary owner"
    );
    return party ? getName(party.person) : "";
  };

  useEffect(() => {
    setDropdownState({
      isOpen1: false,
      isOpen2: false,
      selectedStatus: ["All"],
      selectedClaimType: ["All"],
    });
  }, [data]);

  const {
    rowsForDisplay: claimRows,
    rowPaginationProps: claimsPaginationProps,
  } = useRowPagination(sortedData, [5, 10, 25, "all"]);

  const Dropdown = ({
    label,
    isOpen,
    toggleDropdown,
    options,
    selectedOptions,
    handleSelectionChange,
    dropdownRef,
  }) => (
    <div
      className={`text-xs font-bold text-theme-text-secondary md:col-span-${
        label === "Status" ? "2" : "1"
      } cursor-pointer`}
    >
      <div className="relative inline-block text-left" ref={dropdownRef}>
        <div
          onClick={toggleDropdown}
          className="flex items-center justify-between w-full"
        >
          {label}
          <span>
            <RxCaretDown
              style={{ transform: `${isOpen ? "rotate(180deg)" : ""}` }}
              size={23}
            />
          </span>
        </div>
        {isOpen && (
          <div className="absolute left-0 z-10 w-48 mt-1 origin-top-right bg-white border border-gray-300 rounded-md shadow-lg">
            {options.map((option, index) => (
              <label
                className="ui-checkbox mt-1 flex items-center px-4 py-1"
                key={index}
              >
                <input
                  type="checkbox"
                  value={option}
                  checked={selectedOptions.includes(option)}
                  onChange={() => handleSelectionChange(option)}
                />
                <div className="ui-checkmark">
                  <svg
                    className="ui-icon w-2 h-2 text-white"
                    focusable="false"
                    aria-hidden="true"
                    role="presentation"
                  >
                    <use href="#ui-icon-checkmark"></use>
                  </svg>
                </div>
                <span>{option}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <div className="ui-card-bar relative mt-4">
        <div className="px-3 py-2 lg:p-4">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-2 xl:gap-2">
            <Dropdown
              label="Status"
              isOpen={dropdownState.isOpen1}
              toggleDropdown={() => toggleDropdown("isOpen1")}
              options={uniqueStatusTexts}
              selectedOptions={dropdownState.selectedStatus}
              handleSelectionChange={(option) =>
                handleSelectionChange("selectedStatus", option)
              }
              dropdownRef={dropdownRef1}
            />
            <Dropdown
              label="Claim Type"
              isOpen={dropdownState.isOpen2}
              toggleDropdown={() => toggleDropdown("isOpen2")}
              options={uniqueClaimType}
              selectedOptions={dropdownState.selectedClaimType}
              handleSelectionChange={(option) =>
                handleSelectionChange("selectedClaimType", option)
              }
              dropdownRef={dropdownRef2}
            />
            <div className="text-xs font-bold text-theme-text-secondary md:col-span-1">
              Policy #
            </div>
            <div className="flex items-center text-xs font-bold text-theme-text-secondary md:col-span-1">
              Claim #
            </div>
            <div
              className="flex items-center text-xs font-bold text-theme-text-secondary md:col-span-1 cursor-pointer"
              onClick={() => requestSort("dateReceived")}
            >
              Date Received
              <span>
                <RxCaretDown
                  style={{
                    transform: `${
                      sortConfig.key === "dateReceived" &&
                      sortConfig.direction === "asc"
                        ? "rotate(180deg)"
                        : ""
                    }`,
                  }}
                  size={23}
                />
              </span>
            </div>
          </div>
        </div>
      </div>
      {claimRows.map((item, index) => (
        <ClaimsList
          key={item.taskID || index}
          data={item}
          setOpenChatWindow={setOpenChatWindow}
          type={type}
          primaryOwner={getPrimaryOwnerFullName(item.contractID)}
        />
      ))}
      {claimRows.length > 0 && <RowPagination {...claimsPaginationProps} />}
    </>
  );
};
